import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ChevronDown, Play, Lock, ListPlus } from "lucide-react";
import { toast } from "sonner";
import { getRelease } from "../lib/api";
import { Reveal } from "../components/Motion";
import { usePlayer } from "../context/PlayerContext";
import { liveStreamingLinks } from "../lib/streaming";

const PLAYABLE = ["FULL_PUBLIC", "TEASER", "PREVIEW", "PURCHASE_REQUIRED", "INNER_CIRCLE", "VAULT_ENTITLEMENT"];

function TrackRow({ t, onPlay, onQueue, isCurrent }) {
  const [open, setOpen] = useState(false);
  const ed = t.editorial || {};
  const playable = PLAYABLE.includes(t.availability);
  const lockedType = !["FULL_PUBLIC", "PREVIEW", "TEASER"].includes(t.availability);

  return (
    <li className="border-t border-white/10" data-testid={`track-${t.trackNumber}`}>
      <div className="flex items-center gap-4 py-5">
        <button
          onClick={() => onPlay(t)}
          data-testid={`track-${t.trackNumber}-play`}
          aria-label={lockedType ? `${t.title} — ${t.lockLabel}` : `Play ${t.title}`}
          className={`w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
            isCurrent
              ? "border-gold bg-gold text-black"
              : lockedType
              ? "border-white/15 text-white/30 hover:border-white/40 hover:text-white/60"
              : "border-gold/50 text-gold hover:bg-gold hover:text-black"
          }`}
        >
          {lockedType ? <Lock size={12} /> : <Play size={12} className="ml-0.5" />}
        </button>
        {playable && (
          <button onClick={() => onQueue(t)} data-testid={`track-${t.trackNumber}-queue`}
            aria-label={`Add ${t.title} to queue`}
            className="w-9 h-9 rounded-full border border-white/15 text-white/40 hidden sm:flex items-center justify-center hover:border-gold hover:text-gold transition-colors shrink-0">
            <ListPlus size={13} />
          </button>
        )}
        <button
          onClick={() => setOpen(!open)}
          data-testid={`track-${t.trackNumber}-toggle`}
          aria-expanded={open}
          className="group flex-1 flex items-center justify-between text-left min-w-0"
        >
          <div className="flex items-center gap-5 min-w-0">
            <span className="font-mono text-[11px] text-gold w-6 shrink-0">{String(t.trackNumber).padStart(2, "0")}</span>
            <span className={`font-serif text-2xl md:text-3xl font-light truncate transition-colors group-hover:text-gold ${lockedType ? "text-white/60" : "text-white"}`}>
              {t.title}
            </span>
            {t.explicit && <span className="font-mono text-[8px] tracking-[0.2em] text-white/30 border border-white/15 px-1.5 py-0.5 shrink-0">E</span>}
          </div>
          <div className="flex items-center gap-4 shrink-0 ml-4">
            {["PREVIEW", "TEASER"].includes(t.availability) && (
              <span className="font-mono text-[8px] tracking-[0.25em] text-gold/80 border border-gold/30 px-2.5 py-1 hidden sm:block">
                TEASER{t.teaserDuration || t.previewDuration ? ` · ${t.teaserDuration || t.previewDuration}″` : ""}
              </span>
            )}
            {lockedType && (
              <span className="font-mono text-[8px] tracking-[0.25em] text-white/30 border border-white/10 px-2.5 py-1 hidden sm:block" data-testid={`track-${t.trackNumber}-lock-label`}>
                {t.lockLabel}
              </span>
            )}
            <span className="font-mono text-[11px] text-white/40">{t.duration}</span>
            <ChevronDown size={16} className={`transition-transform duration-500 ${open ? "rotate-180 text-gold" : "text-white/40"}`} />
          </div>
        </button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pb-6 pt-2 md:pl-12">
              <div data-testid={`track-${t.trackNumber}-lyrics`}>
                <p className="font-mono text-[10px] tracking-[0.3em] text-gold/80 mb-4">LYRICS</p>
                {ed.lyrics ? (
                  <p className="font-serif text-lg font-light leading-relaxed text-white/80 whitespace-pre-line">{ed.lyrics}</p>
                ) : (
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/25">NOT YET PUBLISHED.</p>
                )}
              </div>
              <div data-testid={`track-${t.trackNumber}-meaning`}>
                <p className="font-mono text-[10px] tracking-[0.3em] text-gold/80 mb-4">BEHIND THE MEANING</p>
                {ed.meaning ? (
                  <p className="text-white/60 leading-relaxed">{ed.meaning}</p>
                ) : (
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/25">NOT YET PUBLISHED.</p>
                )}
              </div>
            </div>
            {t.slug && (
              <div className="pb-10 md:pl-12">
                <Link to={`/track/${t.slug}`} data-testid={`track-${t.trackNumber}-page-link`}
                  className="font-mono text-[10px] tracking-[0.25em] text-white/40 hover:text-gold transition-colors">
                  OPEN TRACK PAGE →
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}

export default function ReleaseDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [release, setRelease] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"], layoutEffect: false });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const { playTrack, addToQueue, isCurrent, notice } = usePlayer();
  const ctxOf = () => ({
    releaseSlug: slug, releaseTitle: release?.title,
    artwork: release?.artwork, dimension: release?.dimension,
  });
  const onPlay = (t) => playTrack(t, { ...ctxOf(), queue: release?.trackList });
  const onQueue = (t) => { if (addToQueue(t, ctxOf())) toast.success("Added to queue."); };

  useEffect(() => {
    window.scrollTo(0, 0);
    getRelease(slug)
      .then((r) => {
        setRelease(r);
        document.title = `${r.title} — Motion & Meaning`;
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6" data-testid="release-notfound">
        <p className="font-serif text-4xl">This dimension does not exist.</p>
        <Link to="/music" className="font-mono text-[11px] tracking-[0.2em] text-gold">← BACK TO MUSIC</Link>
      </div>
    );

  if (!release) return <div className="min-h-screen" />;

  return (
    <div data-testid="release-detail-page">
      {/* Full-width artwork */}
      <section ref={ref} className="relative h-[80svh] overflow-hidden">
        <motion.img style={{ y, scale: 1.1 }} src={release.artwork} alt={release.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />
        <div className="absolute bottom-0 left-0 right-0 max-w-[1440px] mx-auto px-6 md:px-12 pb-16">
          {release.dimension === "01" && release.titleParts && (
            <Link
              to="/dimension-i"
              data-testid="release-dimension-link"
              className="inline-block font-mono text-[10px] tracking-[0.35em] text-silver/80 hover:text-white transition-colors mb-5"
            >
              ← DIMENSION I — THE PHYSICAL
            </Link>
          )}
          <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-4">
            {release.releaseType.toUpperCase()} · {release.status}
          </p>
          {release.titleParts ? (
            <>
              <p className="font-mono text-[11px] tracking-[0.5em] text-white/50">{release.titleParts.project}</p>
              <p className="font-serif text-2xl sm:text-3xl font-light tracking-[0.25em] text-white/80 mt-3">
                {release.titleParts.chapter}
              </p>
              <h1 className="di-metal font-serif text-6xl sm:text-8xl lg:text-9xl font-light leading-[0.9] mt-2">
                {release.titleParts.name}
              </h1>
            </>
          ) : (
            <h1 className="font-serif text-5xl sm:text-7xl lg:text-8xl font-light leading-[0.9] text-white max-w-4xl">
              {release.title}
            </h1>
          )}
        </div>
      </section>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          {/* Left: statement + tracks */}
          <div className="md:col-span-7">
            <Reveal>
              {release.statement && (
                <>
                  <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 mb-6">ARTISTIC STATEMENT</p>
                  <p className="font-serif text-2xl md:text-3xl font-light leading-snug text-white/90">{release.statement}</p>
                </>
              )}
              {release.description && (
                <p className="text-white/60 leading-relaxed mt-8">{release.description}</p>
              )}
            </Reveal>

            <Reveal delay={0.1}>
              <div className="mt-20">
                <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 mb-8">TRACK LISTING</p>
                <ul>
                  {release.trackList.map((t) => (
                    <TrackRow key={t.trackNumber} t={t} onPlay={onPlay} onQueue={onQueue}
                      isCurrent={isCurrent(slug, t.trackNumber) && !notice} />
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Right: meta */}
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="border border-white/10 p-8 md:p-10 space-y-8">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-2">RELEASE DATE</p>
                  <p className="text-white text-lg">{release.releaseDate}</p>
                </div>
                {(release.credits || []).length > 0 && (
                  <>
                    <div className="gold-line" />
                    <div data-testid="release-credits">
                      <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-3">CREDITS</p>
                      {release.credits.map((c, i) => (
                        <div key={i} className="flex justify-between text-sm py-1">
                          <span className="text-white/40">{c.role}</span>
                          <span className="text-white/80">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {liveStreamingLinks(release.streamingLinks).length > 0 && (
                  <>
                    <div className="gold-line" />
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.25em] text-white/40 mb-4">LISTEN</p>
                      <div className="flex flex-col gap-3" data-testid="release-streaming-links">
                        {liveStreamingLinks(release.streamingLinks).map((s, i) => (
                          <a
                            key={i}
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`stream-${(s.platform || "link").toLowerCase().replace(/\s+/g, "-")}`}
                            className="flex items-center justify-between border border-white/15 px-5 py-3 font-mono text-[11px] tracking-[0.2em] text-white/70 hover:border-gold hover:text-gold transition-colors"
                          >
                            {(s.label || s.platform).toUpperCase()} <span>→</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="w-full bg-gold text-black font-mono text-[11px] tracking-[0.25em] py-4 hover:bg-white transition-colors"
                  data-testid="release-purchase"
                >
                  PURCHASE
                </button>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-24">
          <Link to="/music" data-testid="back-to-music" className="font-mono text-[11px] tracking-[0.2em] text-white/50 hover:text-gold transition-colors">
            ← BACK TO CATALOG
          </Link>
        </div>
      </div>
    </div>
  );
}
